/**
 * Simulates various network conditions for testing.
 * Implements throttling, latency, and packet loss simulation.
 */

export interface NetworkProfile {
  name: string;
  downloadKbps: number;
  uploadKbps: number;
  latencyMs: number;
  packetLossPercent: number;
}

export const NETWORK_PROFILES: Record<string, NetworkProfile> = {
  'perfect': {
    name: 'Perfect (No throttling)',
    downloadKbps: Infinity,
    uploadKbps: Infinity,
    latencyMs: 0,
    packetLossPercent: 0,
  },
  'fiber': {
    name: 'Fiber (100 Mbps)',
    downloadKbps: 100000,
    uploadKbps: 100000,
    latencyMs: 5,
    packetLossPercent: 0,
  },
  'cable': {
    name: 'Cable (50 Mbps down / 10 Mbps up)',
    downloadKbps: 50000,
    uploadKbps: 10000,
    latencyMs: 20,
    packetLossPercent: 0.1,
  },
  'dsl': {
    name: 'DSL (10 Mbps down / 2 Mbps up)',
    downloadKbps: 10000,
    uploadKbps: 2000,
    latencyMs: 50,
    packetLossPercent: 0.5,
  },
  '4g': {
    name: '4G LTE (20 Mbps)',
    downloadKbps: 20000,
    uploadKbps: 10000,
    latencyMs: 50,
    packetLossPercent: 1,
  },
  '3g': {
    name: '3G (2 Mbps)',
    downloadKbps: 2000,
    uploadKbps: 500,
    latencyMs: 100,
    packetLossPercent: 2,
  },
  'slow-3g': {
    name: 'Slow 3G (400 Kbps)',
    downloadKbps: 400,
    uploadKbps: 100,
    latencyMs: 300,
    packetLossPercent: 5,
  },
  'edge': {
    name: 'EDGE (100 Kbps)',
    downloadKbps: 100,
    uploadKbps: 50,
    latencyMs: 500,
    packetLossPercent: 10,
  },
  'congested': {
    name: 'Congested Network',
    downloadKbps: 5000,
    uploadKbps: 1000,
    latencyMs: 200,
    packetLossPercent: 5,
  },
  'model-upload': {
    name: 'Model Upload (Good upload)',
    downloadKbps: 10000,
    uploadKbps: 50000,
    latencyMs: 10,
    packetLossPercent: 0,
  },
  'artist-download': {
    name: 'Artist Download (Good download)',
    downloadKbps: 50000,
    uploadKbps: 5000,
    latencyMs: 10,
    packetLossPercent: 0,
  },
};

export type ProfileName = keyof typeof NETWORK_PROFILES;

interface ThrottledChunk {
  data: ArrayBuffer;
  scheduledTime: number;
  callback: (data: ArrayBuffer) => void;
}

export class BandwidthSimulator {
  private profile: NetworkProfile;
  private uploadQueue: ThrottledChunk[] = [];
  private downloadQueue: ThrottledChunk[] = [];
  private isProcessing = false;
  private bytesUploadedThisSecond = 0;
  private bytesDownloadedThisSecond = 0;
  private lastResetTime = Date.now();
  private stats = {
    totalBytesSent: 0,
    totalBytesReceived: 0,
    droppedPackets: 0,
    averageLatency: 0,
  };

  constructor(profile: NetworkProfile | ProfileName = 'perfect') {
    this.profile = typeof profile === 'string' ? NETWORK_PROFILES[profile] : profile;
  }

  setProfile(profile: NetworkProfile | ProfileName): void {
    this.profile = typeof profile === 'string' ? NETWORK_PROFILES[profile] : profile;
    console.log(`Network profile set to: ${this.profile.name}`);
  }

  getProfile(): NetworkProfile {
    return this.profile;
  }

  /**
   * Simulates sending data through the network.
   * Returns a promise that resolves when the data would be "received".
   */
  async send(data: ArrayBuffer): Promise<ArrayBuffer> {
    // Check for packet loss
    if (Math.random() * 100 < this.profile.packetLossPercent) {
      this.stats.droppedPackets++;
      throw new Error('Packet lost (simulated)');
    }

    // Calculate delay based on bandwidth and latency
    const sizeKb = data.byteLength / 1024;
    const transferTimeMs = this.profile.uploadKbps === Infinity
      ? 0
      : (sizeKb / this.profile.uploadKbps) * 1000;

    const totalDelayMs = this.profile.latencyMs + transferTimeMs;

    // Add jitter (±20% of latency)
    const jitter = this.profile.latencyMs * 0.2 * (Math.random() - 0.5);
    const finalDelay = Math.max(0, totalDelayMs + jitter);

    this.stats.totalBytesSent += data.byteLength;

    return new Promise((resolve) => {
      setTimeout(() => {
        resolve(data);
      }, finalDelay);
    });
  }

  /**
   * Simulates receiving data through the network.
   */
  async receive(data: ArrayBuffer): Promise<ArrayBuffer> {
    // Check for packet loss
    if (Math.random() * 100 < this.profile.packetLossPercent) {
      this.stats.droppedPackets++;
      throw new Error('Packet lost (simulated)');
    }

    const sizeKb = data.byteLength / 1024;
    const transferTimeMs = this.profile.downloadKbps === Infinity
      ? 0
      : (sizeKb / this.profile.downloadKbps) * 1000;

    const totalDelayMs = this.profile.latencyMs + transferTimeMs;
    const jitter = this.profile.latencyMs * 0.2 * (Math.random() - 0.5);
    const finalDelay = Math.max(0, totalDelayMs + jitter);

    this.stats.totalBytesReceived += data.byteLength;

    return new Promise((resolve) => {
      setTimeout(() => {
        resolve(data);
      }, finalDelay);
    });
  }

  /**
   * Wraps a WebSocket to simulate network conditions.
   */
  wrapWebSocket(socket: WebSocket): WebSocket {
    const originalSend = socket.send.bind(socket);

    socket.send = (data: string | ArrayBufferLike | Blob | ArrayBufferView) => {
      if (this.profile.name === 'Perfect (No throttling)') {
        originalSend(data);
        return;
      }

      // Convert to ArrayBuffer for size calculation
      let size: number;
      if (typeof data === 'string') {
        size = new TextEncoder().encode(data).length;
      } else if (data instanceof Blob) {
        size = data.size;
      } else if (data instanceof ArrayBuffer) {
        size = data.byteLength;
      } else {
        size = (data as ArrayBufferView).byteLength;
      }

      // Check for packet loss
      if (Math.random() * 100 < this.profile.packetLossPercent) {
        this.stats.droppedPackets++;
        console.log('[BandwidthSimulator] Dropped packet (simulated)');
        return;
      }

      // Calculate delay
      const sizeKb = size / 1024;
      const transferTimeMs = (sizeKb / this.profile.uploadKbps) * 1000;
      const totalDelay = this.profile.latencyMs + transferTimeMs;

      this.stats.totalBytesSent += size;

      setTimeout(() => {
        originalSend(data);
      }, totalDelay);
    };

    return socket;
  }

  /**
   * Get current statistics
   */
  getStats() {
    return { ...this.stats };
  }

  /**
   * Reset statistics
   */
  resetStats(): void {
    this.stats = {
      totalBytesSent: 0,
      totalBytesReceived: 0,
      droppedPackets: 0,
      averageLatency: 0,
    };
  }

  /**
   * Estimates how long it would take to send a given amount of data
   */
  estimateTransferTime(bytes: number, direction: 'upload' | 'download'): number {
    const kbps = direction === 'upload' ? this.profile.uploadKbps : this.profile.downloadKbps;
    if (kbps === Infinity) return this.profile.latencyMs;

    const sizeKb = bytes / 1024;
    return this.profile.latencyMs + (sizeKb / kbps) * 1000;
  }

  /**
   * Returns the effective bandwidth for the current profile
   */
  getEffectiveBandwidth(): { uploadMbps: number; downloadMbps: number } {
    return {
      uploadMbps: this.profile.uploadKbps === Infinity ? Infinity : this.profile.uploadKbps / 1000,
      downloadMbps: this.profile.downloadKbps === Infinity ? Infinity : this.profile.downloadKbps / 1000,
    };
  }
}

// Singleton instance for global network simulation
let globalSimulator: BandwidthSimulator | null = null;

export function getGlobalBandwidthSimulator(): BandwidthSimulator {
  if (!globalSimulator) {
    globalSimulator = new BandwidthSimulator('perfect');
  }
  return globalSimulator;
}

export function setGlobalNetworkProfile(profile: ProfileName | NetworkProfile): void {
  getGlobalBandwidthSimulator().setProfile(profile);
}
